import styled, { keyframes } from "styled-components";
import type { ThemeMode } from "../types";

type ThemeModeProps = {
    themeMode?: ThemeMode;
};

export const MediaPlaceholder = styled.div<ThemeModeProps & { ratio?: string }>`
    position: relative;
    width: 100%;
    aspect-ratio: ${p => p.ratio || "16 / 9"};
    border-radius: var(--calendar-radius-card);
    border: 1px solid var(--calendar-border);
    background: ${p => p.themeMode === "dark"
        ? "linear-gradient(135deg, rgba(109,116,255,0.16), rgba(66,231,182,0.08))"
        : "linear-gradient(135deg, rgba(91,110,245,0.14), rgba(66,231,182,0.12))"};
    overflow: hidden;
    display: grid;
    place-items: center;
`;

export const goalShimmer = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(240%); }
`;

export const confettiDrift = keyframes`
    0% { transform: translateY(-18px) rotate(0deg); opacity: 0; }
    15% { opacity: 1; }
    100% { transform: translateY(110px) rotate(320deg); opacity: 0; }
`;

export const GoalProgressWrap = styled.div`
    position: relative;
    display: grid;
    gap: 0.7rem;
`;

export const GoalProgressHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
`;

export const GoalProgressTrack = styled.div<ThemeModeProps>`
    position: relative;
    width: 100%;
    height: 16px;
    border-radius: var(--calendar-radius-badge);
    overflow: hidden;
    background: ${p => p.themeMode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(148,163,184,0.18)"};
`;

export const GoalProgressFill = styled.div<{ progress: number }>`
    position: relative;
    height: 100%;
    width: ${p => `${p.progress}%`};
    min-width: ${p => p.progress > 0 ? "22px" : "0"};
    border-radius: inherit;
    background: linear-gradient(90deg, #5b6ef5 0%, #7c3aed 46%, #42e7b6 100%);
    transition: width 320ms ease;
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        inset: 0 auto 0 -30%;
        width: 28%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.42), transparent);
        animation: ${goalShimmer} 1.9s linear infinite;
    }
`;

export const GoalProgressMeta = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 11px;
    color: var(--calendar-text-soft);
    font-weight: 700;
`;

export const ConfettiLayer = styled.div`
    pointer-events: none;
    position: absolute;
    inset: -6px 0 auto;
    height: 120px;
    overflow: hidden;
`;

export const ConfettiPiece = styled.span<{ left: number; color: string; duration: number; delay: number }>`
    position: absolute;
    top: 0;
    left: ${p => `${p.left}%`};
    width: 8px;
    height: 16px;
    border-radius: var(--calendar-radius-cell);
    background: ${p => p.color};
    animation: ${confettiDrift} ${p => `${p.duration}s`} linear infinite;
    animation-delay: ${p => `${p.delay}s`};
`;

export const StackList = styled.div`
    display: grid;
    gap: 0.6rem;
`;

export const StackListItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 0.8rem 0.9rem;
    border-radius: var(--calendar-radius-card);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
`;

export const StackListNumber = styled.div`
    width: 24px;
    height: 24px;
    border-radius: var(--calendar-radius-control);
    background: var(--calendar-accent-soft);
    color: var(--calendar-accent);
    font-size: 11px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
`;

export const StackListText = styled.div`
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    color: var(--calendar-text);
`;

export const DetailRows = styled.div`
    display: grid;
    gap: 0.55rem;
`;
